import React from "react";
import { ClipboardList, FileSearch, ShieldCheck, Target } from "lucide-react";
import { resolveExampleEvidence } from "@/lib/capExampleEvidence";

export interface RemediationGuidanceFinding {
  control_code?: string | null;
  source_type?: string | null;
  source_key?: string | null;
  recommended_action?: string | null;
  recommended_measures?: string | null;
  remediation_path?: string | null;
  recommended_additional_evidence?: string | null;
  example_evidence?: string[] | null;
}

interface Props {
  finding: RemediationGuidanceFinding;
  serviceModels?: string[];
  hideHeader?: boolean;
}


function Block({
  title,
  icon: Icon,
  children,
  emptyLabel = "—",
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: React.ReactNode;
  emptyLabel?: string;
}) {
  return (
    <div>
      <div className="text-[11px] font-semibold text-foreground mb-1 flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        {title}
      </div>
      <div className="text-xs text-foreground whitespace-pre-wrap">
        {children ?? <span className="text-muted-foreground italic">{emptyLabel}</span>}
      </div>
    </div>
  );
}

export const RemediationGuidance: React.FC<Props> = ({ finding, serviceModels = [], hideHeader = false }) => {
  const required = (finding.recommended_action || "").trim();
  const measures = (finding.recommended_measures || "").trim();
  const path = (finding.remediation_path || "").trim();
  const extra = (finding.recommended_additional_evidence || "").trim();

  const resolved =
    finding.example_evidence && finding.example_evidence.length > 0
      ? {
          items: finding.example_evidence,
          serviceModelGroups: resolveExampleEvidence({
            controlCode: finding.control_code,
            sourceType: finding.source_type,
            sourceKey: finding.source_key,
            serviceModels,
          }).serviceModelGroups,
        }
      : resolveExampleEvidence({
          controlCode: finding.control_code,
          sourceType: finding.source_type,
          sourceKey: finding.source_key,
          serviceModels,
        });

  const coreSet = new Set(resolved.items.map((i) => i.trim().toLowerCase()));

  return (
    <section className={hideHeader ? "space-y-3" : "rounded-md border bg-background p-3 space-y-3"}>
      {!hideHeader && (
        <header className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Remediation Guidance</h3>
        </header>
      )}


      <Block title="Required Action" icon={ClipboardList} emptyLabel="No required action recorded.">
        {required || null}
      </Block>

      <Block
        title="Recommended Measures"
        icon={ClipboardList}
        emptyLabel="No recommended measures recorded."
      >
        {measures || null}
      </Block>

      <Block title="Remediation Path" icon={Target} emptyLabel="No remediation path recorded.">
        {path || null}
      </Block>

      <Block
        title="Recommended Additional Evidence"
        icon={FileSearch}
        emptyLabel="No additional evidence recommendations."
      >
        {extra || null}
      </Block>

      <div>
        <div className="text-[11px] font-semibold text-foreground mb-1 flex items-center gap-1.5">
          <FileSearch className="h-3.5 w-3.5 text-muted-foreground" />
          Example Evidence
          {finding.control_code && (
            <span className="text-[10px] font-normal text-muted-foreground">
              (control {finding.control_code})
            </span>
          )}
        </div>
        <div className="space-y-3 text-xs">
          {resolved.items.length > 0 ? (
            <div>
              <div className="text-[11px] font-medium text-foreground mb-0.5">
                Core Evidence Examples
              </div>
              <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                {resolved.items.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-muted-foreground italic">No example evidence available.</p>
          )}
          {(resolved.serviceModelGroups ?? []).map((g) => {
            const deduped = g.items.filter((it) => !coreSet.has(it.trim().toLowerCase()));
            if (deduped.length === 0) return null;
            return (
              <div key={g.model}>
                <div className="text-[11px] font-medium text-foreground mb-0.5">
                  Additional Evidence for {g.model}
                </div>
                <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                  {deduped.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RemediationGuidance;
